import { Document, Page, StyleSheet, Text, View, Link } from "@react-pdf/renderer";
import React from "react";

import {
	AggregateDataField,
	AggregateDataFieldValue,
	DataFieldValue,
	EntryGroup,
	ListingGroup,
	ListDataFieldValue,
	OrderedDataFieldValue,
	OrderedEntryGroup,
	OrderedListingGroup,
	SectionVersion,
	StringDataFieldValue,
	TextDataFieldValue
} from "lib/data/model";
import { DataFieldTemplateService, UtilityService } from "lib/services";
import { concat } from "lodash";

interface IPDFProps {
	section: SectionVersion;
}

// Create styles
const styles = StyleSheet.create({
	pageWrapper: {
		flexDirection: "column",
		backgroundColor: "#E4E4E4",
		fontFamily: "Helvetica",
		padding: 10,
		justifyContent: "space-between"
	},
	parentTitle: {
		fontSize: 7,
		marginTop: 10,
		marginRight: 10,
		marginBottom: -8,
		marginLeft: 10
	},
	mainTitle: {
		marginTop: 10,
		marginRight: 10,
		marginBottom: 0,
		marginLeft: 10,
		fontFamily: "Helvetica-Bold"
	},
	metadataWrapper: {
		marginTop: 10,
		marginRight: 10,
		marginBottom: 0,
		marginLeft: 10,
		flexDirection: "row",
		flexWrap: "wrap",
		fontSize: 10
	},
	metadataItemWrapper: {
		marginRight: 10,
		marginBottom: 5
	},
	metadataItemTitle: {
		fontFamily: "Helvetica-Bold"
	},
	metadataItemContent: {

	},
	groupWrapper: {
		flexDirection: "column",
		marginTop: 10,
		marginRight: 0,
		marginBottom: 10,
		marginLeft: 10,
		fontSize: 10
	},
	groupTitle: {
		fontFamily: "Helvetica-Bold",
		fontSize: 12,
		marginBottom: 5
	},
	groupTableWrapper: {
		flexDirection: "row",
		flexGrow: 1
	},
	groupTableColumnWrapper: {
		flexDirection: "column",
		flexGrow: 1
	},
	groupTableColumnHeader: {
		fontFamily: "Helvetica-Bold"
	},
	groupTableColumnItem: {

	},
	footerContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-around"
	},
	footerItem: {
		fontSize: 12
	}
});

export const PDF = (props: IPDFProps) => {

	// Get All Pages
	const pages = renderSection(props.section);

	// Check Pages
	if (pages.length > 0) {

		return (
			<Document title="Directory">

				{
					pages.map((page, index) => page(index + 1))
				}

			</Document>
		);
	}
	else {

		return (
			<Document title="Directory">

				<Page>
					<Text>Nothing To Report</Text>
				</Page>

			</Document>
		);
	}
};

const renderSection = (section: SectionVersion, parentName: string = ""): Array<((actualPageNum: number) => React.ReactNode)> => {

	// Initialize Node Array
	const pages: Array<((actualPageNum: number) => React.ReactNode)> = [];

	// Check For Section Contents
	if (section.metadata.length > 0
		|| section.entryGroups.length > 0
		|| section.listingGroups.length > 0
		|| section.sections.length > 0) {

		// Render Primary Page
		pages.push(renderPage(section, parentName));
	}

	// Loop Through Child Sections
	for (const childSection of section.sections.map((child) => child.item.published!)) {

		// Render Child Section
		pages.push(...renderSection(childSection, (parentName !== "" ? `${parentName} > ${section.name}`.trim() : section.name)));
	}

	// Return Pages
	return pages;
};

const renderPage = (section: SectionVersion, parentName: string = ""): ((actualPageNum: number) => React.ReactNode) => {

	return (actualPageNum: number) => {

		return (
			<Page key={actualPageNum} size="Letter" style={styles.pageWrapper} wrap={true}>
				<View>
					<View>

						{(
							parentName !== ""
								?
								<Text style={styles.parentTitle}>{parentName}</Text>
								:
								undefined
						)}

						<Text style={styles.mainTitle}>{section.name}</Text>
					</View>

					{/* Metadata */}
					{(section.metadata.length > 0
						?
						(
							<View style={styles.metadataWrapper}>

								{section.metadata.sort(OrderedDataFieldValue.sort).map((metadata, index) => (

									renderDataItem(metadata.item, true, index)
								))}

							</View>
						)
						:
						undefined
					)}

					{/* Entry Groups & Listing Groups */}
					{concat<OrderedEntryGroup | OrderedListingGroup>(section.entryGroups, section.listingGroups)
						.sort((a, b) => a.order - b.order)
						.map((orderedGroup, groupIndex) => {

							let group: EntryGroup | ListingGroup;
							let groupName: string;
							let groupItems: Array<{ name: string, data: OrderedDataFieldValue[] }>;

							switch (orderedGroup.$type) {

								case OrderedEntryGroup.$type:

									group = (orderedGroup as OrderedEntryGroup).item;
									groupName = group.name;
									groupItems = group.entries.map((orderedItem) => {

										return {
											name: orderedItem.item.name,
											data: orderedItem.data
										};
									});

									break;

								case OrderedListingGroup.$type:
								default:

									group = (orderedGroup as OrderedListingGroup).item;
									groupName = group.name;
									groupItems = group.listings.map((orderedItem) => {

										return {
											name: orderedItem.item.name,
											data: orderedItem.item.data
										};
									});

									break;
							}

							return (

								<View style={styles.groupWrapper} key={groupIndex}>

									<Text style={styles.groupTitle}>
										{groupName}
									</Text>

									<View style={styles.groupTableWrapper}>

										<View style={styles.groupTableColumnWrapper}>

											<Text style={styles.groupTableColumnHeader}>
												Name
											</Text>

											{groupItems.map((item, itemIndex) => (

												<Text style={styles.groupTableColumnItem} key={itemIndex}>
													{item.name}
												</Text>
											))}

										</View>

										{group.columns.map((column, columnIndex) => (

											<View style={styles.groupTableColumnWrapper} key={columnIndex}>

												<Text style={styles.groupTableColumnHeader}>
													{column.item.field.name}
												</Text>

												{groupItems.map((item, itemIndex) => {

													const matchingDatum = item.data.find((datum) => datum.item.template.field.id === column.item.field.id);

													if (matchingDatum !== undefined) {

														return (
															<Text style={styles.groupTableColumnItem} key={itemIndex}>
																{renderDataItem(matchingDatum.item, false)}
															</Text>
														);
													}

													return <Text style={styles.groupTableColumnItem} key={itemIndex} />;
												})}

											</View>
										))}
									</View>
								</View>
							);
						})}
				</View>
				<View style={styles.footerContainer}>
					<View style={styles.footerItem}>
					<Text>Page: {actualPageNum}</Text>
					</View>
					<View style={styles.footerItem}>
						<Link src="https://directory.wyo.gov">https://directory.wyo.gov</Link>
					</View>
					<View style={styles.footerItem}>
					<Text>{UtilityService.getCurrentDate()}</Text>
					</View>
				</View>
			</Page>
		);
	};
};

const renderDataItem = (value: DataFieldValue, showTitle: boolean, key?: number): React.ReactNode => {

	// Initialize Data Field Value
	let itemComponent: React.ReactNode = null;

	// Check For Empty Value
	if (value !== undefined) {

		// Check Data Item Type
		switch (value.$type) {

			case StringDataFieldValue.$type:

				// Build Data Field Value Component
				itemComponent = (
					<View style={styles.metadataItemContent}>
						<Text>
							{buildStringValue(value as StringDataFieldValue)}
						</Text>
					</View>
				);

				break;

			case ListDataFieldValue.$type:

				// Build Data Field Value Component
				itemComponent = (
					<View style={styles.metadataItemContent}>
						<Text>
							{buildListValue(value as ListDataFieldValue)}
						</Text>
					</View>
				);

				break;

			case AggregateDataFieldValue.$type:

				// Build Data Field Value Component
				itemComponent = (
					<View style={styles.metadataItemContent}>
						{buildAggregateValue(value as AggregateDataFieldValue).map((childValue, index) => (
							<Text key={index}>
								{childValue}
							</Text>
						))}
					</View>
				);

				break;

			case TextDataFieldValue.$type:

				// Build Data Field Value Component
				itemComponent = (
					<View style={styles.metadataItemContent}>
						<Text>
							{buildTextValue(value as TextDataFieldValue)}
						</Text>
					</View>
				);

				break;
		}
	}

	return (

		<View key={key} style={styles.metadataItemWrapper}>
			{(
				showTitle
					?
					(
						<View style={styles.metadataItemTitle}>
							<Text>
								{value.template.field.name.replace(/aggregate/gi, "").trim()}
							</Text>
						</View>
					)
					:
					undefined
			)}

			{itemComponent}
		</View>
	);
};

const buildStringValue = (stringValue: StringDataFieldValue): string => {

	// Initialize Value
	let processedValue: string;

	// Check For Template
	if (stringValue.template) {

		// Build Complete Value
		processedValue = [
			stringValue.template.stringPrefix,
			stringValue.value,
			stringValue.template.stringAppendage
		].filter((r) => r !== undefined && r !== null).join("").trim();
	}
	else {

		// Set Processed Value
		processedValue = stringValue.value;
	}

	// Return Value
	return processedValue;
};

const buildListValue = (listValue: ListDataFieldValue): string => {

	// Initialize Value
	let processedValue: string;

	// Check For Template
	if (listValue.template) {

		// Build Complete Value
		processedValue = [
			listValue.template.stringPrefix,
			listValue.value.value,
			listValue.template.stringAppendage
		].filter((r) => r !== undefined && r !== null).join("").trim();
	}
	else {

		// Set Processed Value
		processedValue = listValue.value.value;
	}

	// Return Value
	return processedValue;
};

const buildAggregateValue = (aggregateValue: AggregateDataFieldValue): string[] => {

	// Initialize Values
	const values: string[] = [];

	// Check For Sub-Values
	if (aggregateValue.values !== undefined && aggregateValue.values !== null) {

		// Sort Values
		const sortedValues = aggregateValue.values.sort((a, b): number => {

			let aOrder: number = 0;
			let bOrder: number = 0;

			// Check 'A' Order
			if (a.order === 0) {

				// Get 'A' Requirement Item
				const aRequirementItem = DataFieldTemplateService.getRequirementItemFromTemplate((aggregateValue.template.field as AggregateDataField).requirements, a.item.template);

				// Check Requirement Item
				if (aRequirementItem) {

					aOrder = aRequirementItem.initialOrder;
				}
			}

			// Check 'B' Order
			if (b.order === 0) {

				// Get 'B' Requirement Item
				const bRequirementItem = DataFieldTemplateService.getRequirementItemFromTemplate((aggregateValue.template.field as AggregateDataField).requirements, b.item.template);

				// Check Requirement Item
				if (bRequirementItem) {

					bOrder = bRequirementItem.initialOrder;
				}
			}

			return aOrder - bOrder;
		});

		// Loop Through Sorted Aggregate Field Values
		for (const sortedValue of sortedValues) {

			// Check Data Item Type
			switch (sortedValue.item.$type) {

				case StringDataFieldValue.$type:

					// Build Data Field Value Component
					values.push(buildStringValue(sortedValue.item as StringDataFieldValue));

					break;

				case ListDataFieldValue.$type:

					// Build Data Field Value Component
					values.push(buildListValue(sortedValue.item as ListDataFieldValue));

					break;

				case AggregateDataFieldValue.$type:

					// Build Data Field Value Component
					values.push(buildAggregateValue(sortedValue.item as AggregateDataFieldValue).join(" "));

					break;

				case TextDataFieldValue.$type:

					// Build Data Field Value Component
					values.push(buildTextValue(sortedValue.item as TextDataFieldValue));

					break;
			}
		}
	}

	// Return Components
	return values;
};

const buildTextValue = (textValue: TextDataFieldValue): string => {

	// Initialize Value
	let processedValue: string;

	// Check For Template
	if (textValue.template) {

		// Build Complete Value
		processedValue = [
			textValue.template.stringPrefix,
			textValue.value,
			textValue.template.stringAppendage
		].filter((r) => r !== undefined && r !== null).join("").trim();
	}
	else {

		// Set Processed Value
		processedValue = textValue.value;
	}

	// Return Value
	return processedValue;
};
